/**
 * Payment Method Modal Context (PBI #1181)
 *
 * Provides a single, app-wide PaymentMethodModal that auto-opens when any API
 * call returns HTTP 402 with errorCode PAYMENT_METHOD_REQUIRED. Free-tier users
 * need a card on file before placing flower or mailed-card orders — this must
 * not reuse the UpgradeModal subscription pitch.
 *
 * How it works:
 *   1. The apiClient.ts 402 interceptor dispatches `paymentMethod:required`
 *      when the response body includes errorCode PAYMENT_METHOD_REQUIRED.
 *   2. This context listens for that event and opens the add-card modal.
 *   3. Components that need to retry a failed action after the user saves a
 *      card can register a one-shot callback via `registerOnClose`.
 *   4. Components can read `paymentMethodOpened` for action-replay guards.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { LazyPaymentMethodModal } from '../components/subscription/LazyPaymentMethodModal';
import {
  PaymentMethodModalContext,
  PAYMENT_METHOD_REQUIRED_EVENT,
} from './PaymentMethodModalContext';

interface PaymentMethodModalProviderProps {
  children: ReactNode;
}

/**
 * Provider that mounts a single PaymentMethodModal at the app root and exposes
 * open/close controls via context. Listens for the `paymentMethod:required`
 * custom event dispatched by the apiClient interceptor on 402 responses.
 */
export const PaymentMethodModalProvider = ({ children }: PaymentMethodModalProviderProps) => {
  const [opened, setOpened] = useState(false);
  const [reason, setReason] = useState<string | undefined>();

  const onCloseCallbacksRef = useRef<Set<() => void>>(new Set());

  const openPaymentMethod = useCallback((paymentReason?: string) => {
    setOpened((prev) => {
      if (prev) return prev;
      setReason(paymentReason);
      return true;
    });
  }, []);

  const handleClose = useCallback(() => {
    setOpened(false);
    setReason(undefined);

    const callbacks = new Set(onCloseCallbacksRef.current);
    onCloseCallbacksRef.current.clear();
    callbacks.forEach((cb) => {
      try {
        cb();
      } catch (err) {
        console.error('[PaymentMethodModalContext] onClose callback error:', err);
      }
    });
  }, []);

  const registerOnClose = useCallback((cb: () => void) => {
    onCloseCallbacksRef.current.add(cb);
    return () => {
      onCloseCallbacksRef.current.delete(cb);
    };
  }, []);

  useEffect(() => {
    const handlePaymentMethodRequired = (event: Event) => {
      const detail = (event as CustomEvent<{ reason?: string }>).detail;
      openPaymentMethod(detail?.reason);
    };

    window.addEventListener(PAYMENT_METHOD_REQUIRED_EVENT, handlePaymentMethodRequired);
    return () => window.removeEventListener(PAYMENT_METHOD_REQUIRED_EVENT, handlePaymentMethodRequired);
  }, [openPaymentMethod]);

  return (
    <PaymentMethodModalContext.Provider
      value={{ paymentMethodOpened: opened, openPaymentMethod, registerOnClose }}
    >
      {children}
      <LazyPaymentMethodModal opened={opened} onClose={handleClose} reason={reason} />
    </PaymentMethodModalContext.Provider>
  );
};
